if (!customElements.get('scroll-carousel')) {
  class ScrollCarousel extends HTMLElement {
    frame = null;

    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.progressThumb = this.querySelector('[data-carousel-progress]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');

      this.onScroll = this.scheduleUpdate.bind(this);
      this.onPrev = this.step.bind(this, -1);
      this.onNext = this.step.bind(this, 1);

      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.prevButton?.addEventListener('click', this.onPrev);
      this.nextButton?.addEventListener('click', this.onNext);

      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);

      this.update();
    }

    disconnectedCallback() {
      this.track?.removeEventListener('scroll', this.onScroll);
      this.prevButton?.removeEventListener('click', this.onPrev);
      this.nextButton?.removeEventListener('click', this.onNext);
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    /**
     * Scrolls back to the start, e.g. after the consumer changes which slides are visible.
     * @returns {void}
     */
    reset() {
      this.track.scrollTo({ left: 0, behavior: 'instant' });
      this.update();
    }

    /**
     * Moves the track by one slide width plus the gap.
     * @param {number} direction - -1 for previous, 1 for next
     * @returns {void}
     */
    step(direction) {
      const slide = [...this.track.children].find((child) => child.offsetWidth > 0);
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = slide ? slide.offsetWidth + gap : this.track.clientWidth;

      this.track.scrollBy({ left: direction * distance });
    }

    scheduleUpdate() {
      if (this.frame) return;

      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    }

    update() {
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      const isOverflowing = maxScroll > 1;

      this.toggleAttribute('data-overflowing', isOverflowing);
      if (!isOverflowing) return;

      if (this.prevButton) this.prevButton.disabled = scrollLeft <= 1;
      if (this.nextButton) this.nextButton.disabled = scrollLeft >= maxScroll - 1;

      if (this.progressThumb) {
        const visible = clientWidth / scrollWidth;
        const offset = scrollLeft / clientWidth;

        this.style.setProperty('--scroll-carousel-visible', visible.toFixed(4));
        this.style.setProperty('--scroll-carousel-offset', offset.toFixed(4));
      }

      if (this.dotsContainer) this.updateDots(scrollLeft, maxScroll);
    }

    updateDots(scrollLeft, maxScroll) {
      const pageCount = Math.ceil(this.track.scrollWidth / this.track.clientWidth);
      const activeIndex = Math.round((scrollLeft / maxScroll) * (pageCount - 1));

      if (this.dotsContainer.children.length !== pageCount) {
        this.dotsContainer.replaceChildren(
          ...Array.from({ length: pageCount }, () => {
            const dot = document.createElement('span');
            dot.className = 'scroll-carousel__dot';
            return dot;
          })
        );
      }

      for (const [index, dot] of [...this.dotsContainer.children].entries()) {
        dot.classList.toggle('scroll-carousel__dot--active', index === activeIndex);
      }
    }
  }

  customElements.define('scroll-carousel', ScrollCarousel);
}
